<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core";
import { Ref, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const couponsIsVisible = ref(false);
const couponsTarget: Ref<null | HTMLElement> = ref(null);

const marketIsVisible = ref(false);
const marketTarget: Ref<null | HTMLElement> = ref(null);

const servicesIsVisible = ref(false);
const servicesTarget: Ref<null | HTMLElement> = ref(null);

useIntersectionObserver(
  couponsTarget,
  ([{ isIntersecting }]) => {
    couponsIsVisible.value = isIntersecting;
  },
  { threshold: 0.9 }
);

useIntersectionObserver(
  marketTarget,
  ([{ isIntersecting }]) => {
    marketIsVisible.value = isIntersecting;
  },
  { threshold: 0.9 }
);

useIntersectionObserver(
  servicesTarget,
  ([{ isIntersecting }]) => {
    servicesIsVisible.value = isIntersecting;
  },
  { threshold: 0.9 }
);
</script>

<template>
  <section class="landing-spend container">
    <h3>{{ t("home.spend.title") }}</h3>
    <p>{{ t("home.spend.subTitle") }}</p>
    <div class="landing-spend__list">
      <article class="spend-item spend-item_coupons">
        <div ref="couponsTarget" :class="{ visible: couponsIsVisible }">
          <img src="@/assets/images/landing/landing-spend-1.png" alt="" />
        </div>
        <h4>{{ t("home.spend.coupons.title") }}</h4>
        <p>{{ t("home.spend.coupons.subTitle") }}</p>
      </article>
      <article class="spend-item spend-item_market">
        <div ref="marketTarget" :class="{ visible: marketIsVisible }">
          <img src="@/assets/images/landing/landing-spend-2.png" alt="" />
        </div>
        <h4>{{ t("home.spend.market.title") }}</h4>
        <p>
          {{ t("home.spend.market.subTitle") }}
        </p>
      </article>
      <article class="spend-item spend-item_services">
        <div ref="servicesTarget" :class="{ visible: servicesIsVisible }">
          <img src="@/assets/images/landing/landing-spend-3.png" alt="" />
        </div>
        <h4>{{ t("home.spend.services.title") }}</h4>
        <p>{{ t("home.spend.services.subTitle") }}</p>
      </article>
    </div>
  </section>
</template>

<style scoped lang="scss">
.landing-spend {
  > h3 {
    text-align: center;
    @include h3();
    color: var(--color-semantic-content-normal-primary);
  }

  > p {
    color: var(--color-semantic-content-normal-secondary);
    text-align: center;
    margin: calc($base-space * 3) 0 0;
    @include subtitle();
  }

  &__list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: calc($base-space * 8);
    margin: calc($base-space * 16) 0 0;

    @media (max-width: 1279px) {
      grid-template-columns: 1fr;
      margin: calc($base-space * 12) 0 0;
      gap: calc($base-space * 6);
    }

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
      margin: calc($base-space * 8) 0 0;
    }
  }
}

.spend-item {
  > div {
    height: 336px;
    border-radius: 24px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    position: relative;

    @media (max-width: 1279px) {
      height: auto;
      padding: calc($base-space * 8) 0 0;
    }

    &:before,
    &:after {
      opacity: 0;
      transition: 0.5s ease-in;
    }

    &.visible {
      &:before,
      &:after {
        opacity: 1;
      }
    }

    > img {
      width: 214px;
    }
  }

  &_coupons {
    > div {
      background-color: var(--color-non-semantic-background-soft-green);

      &:before {
        content: "";
        width: 3px;
        height: 3px;
        display: block;
        background: url("@/assets/images/landing/elements/dot.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        bottom: 126px;
        left: 37px;

        @media (max-width: 1239px) {
          left: calc(50% - 147px);
        }

        @media (max-width: 374px) {
          left: calc(50% - 128px);
        }
      }

      &:after {
        content: "";
        width: 22px;
        height: 12px;
        display: block;
        background: url("@/assets/images/landing/elements/coin1.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        top: 60px;
        right: 37px;

        @media (max-width: 1239px) {
          right: calc(50% - 147px);
        }

        @media (max-width: 374px) {
          right: calc(50% - 135px);
        }
      }
    }
  }

  &_market {
    > div {
      background-color: var(--color-non-semantic-background-soft-orange);

      &:before {
        content: "";
        width: 10px;
        height: 10px;
        display: block;
        background: url("@/assets/images/landing/elements/heart.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        top: 40px;
        left: 32px;

        @media (max-width: 1239px) {
          left: calc(50% - 144px);
        }

        @media (max-width: 374px) {
          left: calc(50% - 126px);
        }
      }

      &:after {
        content: "";
        width: 20px;
        height: 20px;
        display: block;
        background: url("@/assets/images/landing/elements/percent.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        right: 36px;
        bottom: 50px;

        @media (max-width: 1239px) {
          right: calc(50% - 153px);
        }

        @media (max-width: 374px) {
          right: calc(50% - 136px);
        }
      }
    }
  }

  &_services {
    > div {
      background-color: var(--color-non-semantic-background-soft-blue);

      &:before {
        content: "";
        width: 42px;
        height: 19px;
        display: block;
        background: url("@/assets/images/landing/elements/line.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        top: 26px;
        left: 26px;

        @media (max-width: 1239px) {
          left: calc(50% - 159px);
        }

        @media (max-width: 374px) {
          left: calc(50% - 140px);
        }
      }

      &:after {
        content: "";
        width: 12px;
        height: 12px;
        display: block;
        background: url("@/assets/images/landing/elements/close.svg");
        @include centerBackgroundImageContain();
        position: absolute;
        right: 34px;
        bottom: 78px;

        @media (max-width: 1239px) {
          right: calc(50% - 147px);
        }

        @media (max-width: 374px) {
          right: calc(50% - 132px);
        }
      }
    }
  }

  > h4 {
    @include h4();
    color: var(--color-semantic-content-normal-primary);
    margin: calc($base-space * 4) 0 0;
  }

  > p {
    @include body-l();
    color: var(--color-semantic-content-normal-secondary);
    margin: calc($base-space * 2) 0 0;
  }
}
</style>
